/**
 * Plain checks before a form is sent, for every HelpDesk form (product owner,
 * 7 Oct: "idiot-proof forms").
 *
 * Four things, all client-side and all courtesy — the server stays
 * authoritative and re-checks everything it cares about:
 *
 *   1. How many required fields are still empty, for the count on the
 *      primary button ("Submit · 2 still needed").
 *   2. Which problem to bring into view first when Submit is pressed with
 *      something missing (see `lib/reveal-first-problem.ts`).
 *   3. Plain answer checks — a date already passed where a future one is
 *      expected, a person typed but not picked from the list, an email
 *      address that does not look like one, and text too short to act on.
 *      Each blocks Submit exactly like a missing field, with a friendly line
 *      under the field.
 *      The date and email checks also show as soon as the person leaves the
 *      field (product owner, 8 Oct), not only on Submit (`shownCheck`).
 *   4. Whether a form is long enough to earn a "Here's what IT will get"
 *      summary before it is sent.
 *
 * Pure and import-free, so `node --test` loads it straight from source.
 */

/** The least a required free-text answer can be before it is "obviously too short". */
export const MIN_TEXT_LENGTH = 5;

/** A check that failed; translated under `common:formCheck.problem.*`. */
export type FormCheckKey = "pastDate" | "notPicked" | "email" | "tooShort";

// ─── 1. The count on the button ────────────────────────────────────────────

/** How many of the flags are set: the fields still needed, for the button. */
export function stillNeededCount(flags: readonly boolean[]): number {
  return flags.reduce((total, flag) => (flag ? total + 1 : total), 0);
}

// ─── 2. The first problem ──────────────────────────────────────────────────

/** What the page knows about one candidate, in document order. */
export interface ProblemCandidate {
  /** The field-state tint (`data-highlight`): "required" is still needed. */
  highlight?: string | null;
  /** Marked invalid (`aria-invalid="true"` or `data-invalid`). */
  invalid?: boolean;
  /** A form-level problem (`data-form-problem`, `role="alert"`). */
  problem?: boolean;
  /** Not on screen (a hidden step, a collapsed section): never chosen. */
  hidden?: boolean;
}

/**
 * The index of the first candidate to bring into view, or -1 for none.
 *
 * Document order wins: whichever comes first on the page — a still-needed
 * field, a field with an error, or a form-level alert — is where the person
 * is taken, because that is where they would have looked next anyway.
 */
export function firstProblemIndex(candidates: readonly ProblemCandidate[]): number {
  return candidates.findIndex(
    (candidate) =>
      candidate.hidden !== true &&
      (candidate.highlight === "required" ||
        candidate.invalid === true ||
        candidate.problem === true),
  );
}

// ─── 3. Plain checks ───────────────────────────────────────────────────────

/** Today as `YYYY-MM-DD` in the person's own time zone. */
export function todayIso(now: Date = new Date()): string {
  const pad = (part: number) => String(part).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * Whether a date (or date-time) answer is before today.
 *
 * Compared on the calendar date only, so "today at 08:00" pressed at 10:00 is
 * still fine. Anything that does not start with an ISO date is not judged.
 */
export function isPastDate(value: string | null | undefined, today: string): boolean {
  const day = (value ?? "").trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) {
    return false;
  }
  return day < today;
}

/* A date field asks for the future when it is about when something should
   happen: needed by, start date, effective, valid from, completion. */
const FUTURE_DATE_HINT =
  /need|start|effective|valid.?from|complet|due|deadline|deliver|go.?live|when (do|is|should|would)/i;
/* …and is about the past when it names something already gone: a restore
   point, a last working day, an access removal that may be backdated. */
const PAST_OK_HINT = /restor|recover|last.?work|leav|left|remov|since|birth|happen|occur|lost/i;

/**
 * Whether a date field expects today or later, read off its internal name and
 * (English) label. Conservative: only the obvious "when do you need it" kind,
 * never a field that may legitimately be in the past.
 */
export function expectsFutureDate(names: readonly (string | null | undefined)[]): boolean {
  const text = names.filter((name) => name != null && name !== "").join(" ");
  return FUTURE_DATE_HINT.test(text) && !PAST_OK_HINT.test(text);
}

/** Whether a free-text field asks for an email address, by its name or label. */
export function looksLikeEmailField(names: readonly (string | null | undefined)[]): boolean {
  const text = names.filter((name) => name != null && name !== "").join(" ");
  return /e-?mail/i.test(text) && !/mailbox/i.test(text);
}

const EMAIL_SHAPE = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;.]{2,}$/;

/**
 * Whether text reads as one or more email addresses (separated by commas,
 * semicolons or spaces). A shape check, not a delivery check.
 */
export function isEmailShaped(value: string | null | undefined): boolean {
  const parts = (value ?? "")
    .trim()
    .split(/[,;\s]+/)
    .filter((part) => part !== "");
  return parts.length > 0 && parts.every((part) => EMAIL_SHAPE.test(part));
}

/** Typed, but shorter than `min` characters. Empty is "still needed", not too short. */
export function isTooShort(
  value: string | null | undefined,
  min: number = MIN_TEXT_LENGTH,
): boolean {
  const length = (value ?? "").trim().length;
  return length > 0 && length < min;
}

/**
 * The checks that show as soon as the person leaves the field (product owner,
 * 8 Oct): a date already passed and an email address that does not look like
 * one. The rest wait for Submit — "nobody picked" while the list may still be
 * open, "too short" while they may still be typing.
 */
export const ON_BLUR_CHECKS: readonly FormCheckKey[] = ["pastDate", "email"];

/**
 * Which failed check to show under a field right now, or null.
 *
 * Nothing on a field the person has not left yet (`touched`), unless Submit
 * was pressed (`attempted`), which shows everything. Once shown it follows
 * the answer live, so it clears the moment the answer is corrected.
 */
export function shownCheck(
  problem: FormCheckKey | null | undefined,
  state: { touched: boolean; attempted?: boolean },
): FormCheckKey | null {
  if (problem == null) {
    return null;
  }
  if (state.attempted === true) {
    return problem;
  }
  return state.touched && ON_BLUR_CHECKS.includes(problem) ? problem : null;
}

// ─── 4. The summary step ───────────────────────────────────────────────────

/** A service request form with at least this many questions on screen gets a summary. */
export const REVIEW_MIN_QUESTIONS = 6;

export type ReviewStepInput =
  | {
      form: "service-request";
      /** The offering's questions the person was shown. */
      askedQuestions: number;
      /**
       * Answers handed back instead of filed (the access wizard): it has its
       * own "Check and send" page, so never a second summary.
       */
      collect: boolean;
    }
  | {
      form: "incident";
      /** Follow-up questions with an answer in them. */
      answeredFollowups: number;
    };

/**
 * Whether to show "Here's what IT will get" before sending. Only the longer
 * forms: a service request with more than five questions, and an incident
 * once follow-up questions have been answered. Short forms send directly.
 */
export function needsReviewStep(input: ReviewStepInput): boolean {
  if (input.form === "incident") {
    return input.answeredFollowups > 0;
  }
  return !input.collect && input.askedQuestions >= REVIEW_MIN_QUESTIONS;
}

// ─── The checks on an Ivanti offering's questions ─────────────────────────
//
// Used by the service request form and the chat's request card. Which
// questions get which check is read off the question itself — its type, its
// internal name and its English label — because the catalog is data, not
// code. What this finds in today's catalog (7 Oct):
//
//   - today or later: StartDate (New User IT Setup), NeededBy and
//     EffectiveDate (Business Systems), ValidFrom, "When is this needed",
//     "Urgency, when do you need this done", "Desired completion date",
//     "When do you [need] this to be completed";
//   - left alone: RecoverDate (a restore point), LastWorkingDay and
//     RemoveAccessOn (a leaver, may be backdated);
//   - email shaped: "User email address", ManualEmail;
//   - picked from the list: every directory (person) lookup;
//   - at least five characters: every required multi-line answer.

/** The parts of an `OfferingParameter` the checks read. */
export interface CheckableParameter {
  type?: string;
  required?: boolean;
  internal_name?: string | null;
  name: { text: string; original_text?: string | null };
  lookup?: string | null;
}

/** The parts of an answer the checks read. */
export interface CheckableAnswer {
  value: string | null;
  valueIds: readonly string[];
}

/** The names a question goes by: internal, English and as shown. */
function namesOf(parameter: CheckableParameter): string[] {
  return [parameter.internal_name ?? "", parameter.name.original_text ?? "", parameter.name.text];
}

/**
 * The failing plain check for one answered question, or null. A required
 * question left empty is "still needed" (`isAnswerMissing`), not this.
 *
 * @param unpicked A person lookup with a name typed but nobody picked.
 */
export function checkOfferingAnswer(
  parameter: CheckableParameter,
  answer: CheckableAnswer,
  options: { today: string; unpicked?: boolean },
): FormCheckKey | null {
  if (parameter.lookup === "EMPLOYEE") {
    return options.unpicked === true ? "notPicked" : null;
  }
  const value = answer.value ?? "";
  switch (parameter.type) {
    case "DATE":
    case "DATETIME":
      return expectsFutureDate(namesOf(parameter)) && isPastDate(value, options.today)
        ? "pastDate"
        : null;
    case "TEXT":
      return value.trim() !== "" && looksLikeEmailField(namesOf(parameter)) && !isEmailShaped(value)
        ? "email"
        : null;
    // No length rule on form answers (7 Oct): "MO" is a whole Bluestar area,
    // and short codes are valid answers. Only the incident description keeps one.
    default:
      return null;
  }
}
